/** Per-block data-freshness label -- the shared, consistent "as of {date}"
 * marker every data block on every page renders next to its own header,
 * built from THAT block's own underlying data date, never a single
 * page-level date standing in for every source on the page.
 *
 * WHY THIS EXISTS: different blocks on the same page read from different
 * tables (bars, market_breadth, macro_factors, fii_dii_flows, prediction,
 * ...) that can go stale independently -- a page-level "as of" hides that.
 * This was the exact failure mode that let two real staleness bugs go
 * unnoticed until a user spotted mismatched dates by eye: the daily cron
 * silently failing for a week, and market_breadth lagging bars inside a
 * single pipeline run. A per-block label surfaces that mismatch immediately
 * instead of relying on someone noticing it.
 *
 * `reference` is the date this block's own date is judged against for
 * staleness -- normally the page's freshest known date (its own top-level
 * `as_of`/`data_as_of`, or another block's date on the same page), NOT
 * always today's wall-clock date: a weekend/holiday legitimately has no
 * newer trading-day data, and that must not paint every block "stale" every
 * Saturday. Callers pass whatever their page already knows is the relevant
 * comparison point; this component does no calendar logic of its own. */
export function AsOfBadge({
  date,
  reference,
  label,
}: {
  /** This block's own underlying data date (ISO yyyy-mm-dd), or null/undefined
   * if the block has no data yet (an honest empty state, not a stale one). */
  date: string | null | undefined;
  /** What `date` is judged against for staleness -- typically the page's
   * freshest known date. Omit to render with no stale comparison at all. */
  reference?: string | null;
  /** Optional short prefix naming which underlying series this is, e.g.
   * "bars" / "breadth" / "flows" -- for a block whose header doesn't already
   * make that obvious. */
  label?: string;
}) {
  if (!date) {
    return <span className="as-of-badge as-of-none">no data yet</span>;
  }
  const stale = reference != null && date < reference;
  return (
    <span
      className={`as-of-badge${stale ? " stale" : ""}`}
      title={stale ? `Lags the freshest data on this page (${reference}) -- shown honestly, not hidden` : undefined}
    >
      {label ? `${label} ` : ""}as of {date}
      {stale && <span className="as-of-stale-mark"> ⚠ stale</span>}
    </span>
  );
}
